<template>
  <div class="zones-painter">
    <v-stage
      v-if="zoneWidth && zoneHeight"
      ref="stage"
      :config="{ width: zoneWidth, height: zoneHeight }"
      class="zones-painter__konva"
    >
      <v-layer>
        <!-- Прямоугольник -->
        <v-line
          ref="line"
          :config="{
            points: polygonPoints,
            fill: zoneColor,
            opacity: 0.2,
            stroke: '#000',
            strokeWidth: 2,
            closed: true,
            draggable: true,
            dragBoundFunc: getCorrectLinePosition
          }"
          @dragend="dragEndOnPolygon"
          @mouseout="changeCursor('auto')"
          @mouseover="changeCursor('pointer')"
        />
        <!-- Контрольные точки -->
        <v-circle
          v-for="(point, index) in points"
          :key="index"
          :config="{
            x: point.x,
            y: point.y,
            stroke: '#000',
            strokeWidth: 1,
            fill: zoneColor,
            radius: 7,
            opacity: 0.6,
            draggable: true,
            dragBoundFunc: getCorrectPointPosition
          }"
          @dragmove="dragMoveOnPoint(index)"
          @dragend="dragEndOnPoint"
          @mouseover="changeCursor('pointer')"
          @mouseout="changeCursor('auto')"
        />
      </v-layer>
    </v-stage>
  </div>
</template>

<script>
export default {
  props: {
    /**
     * Ширина полотна для рисования зон.
     */
    zoneWidth: {
      type: Number,
      required: true
    },
    /**
     * Высота полотна для рисования зон.
     */
    zoneHeight: {
      type: Number,
      required: true
    },
    /**
     * Начальное положение точек для зоны.
     */
    initialPoints: {
      type: Array,
      default: () => []
    },
    /**
     * Цвет для зоны.
     */
    zoneColor: {
      type: String,
      default: null
    },
  },
  data() {
    return {
      points: [], // Массив координат и прочих настроек точек зоны.
      isDragging: false, // Флаг для контроля перемещения всей фигуры.
    };
  },
  computed: {
    /**
     * Генерирует массив координат точек для рисования полигона
     */
    polygonPoints() {
      return this.points.flatMap(p => [p.x, p.y]);
    },
  },
  watch: {
    /**
     * Если `initialPoints` изменились, обновляем точки
     */
    initialPoints: {
      deep: true,
      immediate: true,
      handler(newPoints) {
        if (newPoints.length === 4) {
          this.points = [...newPoints];
        } else {
          this.createDefaultRectangle();
        }
      },
    },
  },
  methods: {
    /**
     * Создает квадрат 100x100 по центру
     */
    createDefaultRectangle() {
      const centerX = this.zoneWidth / 2;
      const centerY = this.zoneHeight / 2;
      const size = 100;

      this.points = [
        { x: centerX - size / 2, y: centerY - size / 2 }, // Верхний левый
        { x: centerX + size / 2, y: centerY - size / 2 }, // Верхний правый
        { x: centerX + size / 2, y: centerY + size / 2 }, // Нижний правый
        { x: centerX - size / 2, y: centerY + size / 2 }, // Нижний левый
      ];
    },
    /**
     * Двигаем угловые точки, сохраняя 90°
     */
    dragMoveOnPoint(index) {
      const mousePosition = this.getCorrectPointPosition(this.$refs.stage.getNode().getPointerPosition());

      let x1 = this.points[0].x;
      let y1 = this.points[0].y;
      let x2 = this.points[2].x;
      let y2 = this.points[2].y;

      if (index === 0) {
        // Верхний левый
        x1 = mousePosition.x;
        y1 = mousePosition.y;
      } else if (index === 1) {
        // Верхний правый
        x2 = mousePosition.x;
        y1 = mousePosition.y;
      } else if (index === 2) {
        // Нижний правый
        x2 = mousePosition.x;
        y2 = mousePosition.y;
      } else if (index === 3) {
        // Нижний левый
        x1 = mousePosition.x;
        y2 = mousePosition.y;
      }

      // Обновляем точки с сохранением прямоугольной формы
      this.points = [
        { x: x1, y: y1 }, // Верхний левый
        { x: x2, y: y1 }, // Верхний правый
        { x: x2, y: y2 }, // Нижний правый
        { x: x1, y: y2 }, // Нижний левый
      ];
      this.savePoints()
    },
    /**
     * Завершаем перетаскивание точки
     */
    dragEndOnPoint() {
      this.savePoints()
    },
    /**
     * Двигаем весь прямоугольник
     */
    dragEndOnPolygon() {
      const lineNode = this.$refs.line.getNode();
      const offsetX = lineNode.x();
      const offsetY = lineNode.y();

      this.points = this.points.map(p => ({
        x: p.x + offsetX,
        y: p.y + offsetY,
      }));

      lineNode.x(0);
      lineNode.y(0);

      this.changeCursor("pointer");
      this.savePoints();
      this.isDragging = false;
    },
    /**
     * Коррекция точек (чтобы не выходили за границы)
     */
    getCorrectPointPosition(mousePosition) {
      let x = Math.max(0, Math.min(mousePosition.x, this.zoneWidth));
      let y = Math.max(0, Math.min(mousePosition.y, this.zoneHeight));
      return { x, y };
    },
    /**
     * Коррекция границ при перемещении всей фигуры
     */
    getCorrectLinePosition(mouseOffset) {
      let x = mouseOffset.x;
      let y = mouseOffset.y;

      const minX = Math.min(...this.points.map(p => p.x)) + x;
      const maxX = Math.max(...this.points.map(p => p.x)) + x;
      const minY = Math.min(...this.points.map(p => p.y)) + y;
      const maxY = Math.max(...this.points.map(p => p.y)) + y;

      if (minX < 0) x = -Math.min(...this.points.map(p => p.x));
      if (maxX > this.zoneWidth) x = this.zoneWidth - Math.max(...this.points.map(p => p.x));
      if (minY < 0) y = -Math.min(...this.points.map(p => p.y));
      if (maxY > this.zoneHeight) y = this.zoneHeight - Math.max(...this.points.map(p => p.y));

      return { x, y };
    },
    /**
     * Сохраняем текущие точки
     */
    savePoints() {
      this.$emit("save-points", this.points.map((pointConfig) => ({ x: pointConfig.x, y: pointConfig.y })));
    },
    /**
     * Изменяем курсор
     */
    changeCursor(cursor) {
      document.body.style.cursor = cursor;
    },
  },
};
</script>

<style lang="scss">
.zones-painter {
  position: absolute;
  z-index: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;

  &__konva {
    width: fit-content;
    height: fit-content;
  }
}
</style>
